import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import clsx from 'clsx';
import { ArrowUpRight, Check, CheckCircle2, FileText, Hourglass, RotateCcw, Users, Vote } from 'lucide-react';
import { Avatar, Container, Glow, Section, SectionHeader } from './ui';
import { EASE, Reveal } from './motion';

const MEMBERS = [
  { initials: 'JD', name: 'John Davis', color: 'blue' },
  { initials: 'KS', name: 'Kate S.', color: 'green' },
  { initials: 'MR', name: 'Mike R.', color: 'amber' },
  { initials: 'SM', name: 'Sarah Miller', color: 'purple' },
  { initials: 'AL', name: 'Alex L.', color: 'pink' },
];

const MODES = {
  majority: {
    label: 'Majority',
    required: 3,
    rule: 'More than half of the group signs off before funds move.',
  },
  unanimous: {
    label: 'Unanimous',
    required: MEMBERS.length,
    rule: 'Every single member must approve. Ideal for big-ticket decisions.',
  },
};

const STEP_MS = 850;

const useApprovalSequence = (required, active, runKey) => {
  const reduceMotion = useReducedMotion();
  const [approvals, setApprovals] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    if (reduceMotion) {
      setApprovals(required);
      return undefined;
    }
    setApprovals(0);
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setApprovals(count);
      if (count >= required) clearInterval(id);
    }, STEP_MS);
    return () => clearInterval(id);
  }, [required, active, runKey, reduceMotion]);

  return approvals;
};

const MemberVote = ({ member, state }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="relative">
      <motion.div
        animate={{
          opacity: state === 'skipped' ? 0.35 : 1,
          scale: state === 'approved' ? 1.06 : 1,
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className={clsx(
          'rounded-full p-[3px] transition-colors duration-500',
          state === 'approved'
            ? 'bg-emerald-400 shadow-[0_0_24px_-2px_rgba(52,211,153,0.7)]'
            : 'bg-white/10'
        )}
      >
        <Avatar initials={member.initials} color={member.color} size="md" className={clsx('sm:h-12 sm:w-12 sm:text-sm', state !== 'approved' && 'grayscale-[60%]')} />
      </motion.div>

      <AnimatePresence>
        {state === 'approved' && (
          <motion.span
            key="check"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 18 }}
            className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400 text-ink-950 ring-2 ring-ink-900"
          >
            <Check className="h-3 w-3" strokeWidth={4} />
          </motion.span>
        )}
      </AnimatePresence>
    </div>
    <span
      className={clsx(
        'hidden text-[11px] font-medium transition-colors duration-300 sm:block',
        state === 'approved' && 'text-emerald-300',
        state === 'pending' && 'text-slate-500',
        state === 'skipped' && 'text-slate-600'
      )}
    >
      {state === 'approved' ? 'Approved' : state === 'pending' ? 'Pending' : 'Not needed'}
    </span>
  </div>
);

const WithdrawalCard = ({ mode, approvals }) => {
  const { required } = MODES[mode];
  const isApproved = approvals >= required;

  return (
    <div className="relative">
      <Glow
        className={clsx(
          '-inset-8 transition-colors duration-700',
          isApproved ? 'bg-emerald-500/20' : 'bg-brand-500/15'
        )}
      />
      <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.06] text-white">
              <ArrowUpRight className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-white">Withdrawal Request</p>
              <p className="text-xs text-slate-500">Group Trip Fund · Hawaii 2025</p>
            </div>
          </div>
          <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-slate-300">
            {MODES[mode].label}
          </span>
        </div>

        {/* Amount */}
        <div className="mt-8">
          <p className="text-5xl font-extrabold tabular-nums tracking-tight text-white sm:text-6xl">$1,200.00</p>
          <p className="mt-2 text-sm text-slate-400">
            Beachfront villa deposit · requested by <span className="text-slate-200">John Davis</span>
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-slate-300">
            <FileText className="h-3.5 w-3.5 text-brand-300" />
            villa-invoice.pdf
          </span>
        </div>

        {/* Voters */}
        <div className="surface-inset mt-8 p-5">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 font-medium text-white">
              <Users className="h-4 w-4 text-slate-400" />
              Approvals
            </span>
            <span className="tabular-nums text-slate-400">
              <span className={clsx('font-semibold', isApproved ? 'text-emerald-300' : 'text-white')}>{approvals}</span> of {required} required
            </span>
          </div>

          {/* Segmented progress */}
          <div className="mt-4 flex gap-1.5" aria-hidden="true">
            {MEMBERS.map((m, i) => (
              <div
                key={m.initials}
                className={clsx(
                  'h-1.5 flex-1 overflow-hidden rounded-full',
                  i < required ? 'bg-white/10' : 'bg-white/[0.03]'
                )}
              >
                <motion.div
                  className="h-full origin-left rounded-full bg-emerald-400"
                  initial={false}
                  animate={{ scaleX: i < approvals ? 1 : 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-5 gap-2">
            {MEMBERS.map((member, i) => {
              let state = 'pending';
              if (i < approvals) state = 'approved';
              else if (isApproved) state = 'skipped';
              return <MemberVote key={member.initials} member={member} state={state} />;
            })}
          </div>
        </div>

        {/* Status */}
        <div className="mt-6 h-14" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            {isApproved ? (
              <motion.div
                key="approved"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="flex h-full items-center justify-between rounded-2xl border border-emerald-400/25 bg-emerald-400/10 px-4"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-emerald-300">
                  <CheckCircle2 className="h-5 w-5" />
                  Approved · funds released
                </span>
                <span className="text-xs text-emerald-300/70">Logged to group record</span>
              </motion.div>
            ) : (
              <motion.div
                key="pending"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="flex h-full items-center justify-between rounded-2xl border border-amber-400/20 bg-amber-400/[0.07] px-4"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-amber-300">
                  <Hourglass className="h-5 w-5" />
                  Awaiting approvals
                </span>
                <span className="text-xs tabular-nums text-amber-300/70">
                  {required - approvals} more needed
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const GovernancePreview = () => {
  const [mode, setMode] = useState('majority');
  const [runKey, setRunKey] = useState(0);
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: '0px 0px -120px 0px' });
  const approvals = useApprovalSequence(MODES[mode].required, inView, runKey);

  return (
    <Section id="governance" className="overflow-hidden">
      <Glow className="right-[-12rem] top-1/3 h-[30rem] w-[30rem] bg-violet-600/10" />
      <Container className="relative">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeader
              align="left"
              eyebrow="Group Controls"
              title="Customizable approval workflows"
              description="Spend the money with clear approvals and thorough documentation."
            />

            <Reveal delay={0.2} className="mt-10">
              <div
                role="radiogroup"
                aria-label="Approval rule"
                className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1"
              >
                {Object.entries(MODES).map(([key, m]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={mode === key}
                    onClick={() => {
                      setMode(key);
                      setRunKey((k) => k + 1);
                    }}
                    className={clsx(
                      'relative min-h-[44px] cursor-pointer rounded-full px-6 text-sm font-semibold transition-colors duration-200',
                      mode === key ? 'text-ink-950' : 'text-slate-400 hover:text-white'
                    )}
                  >
                    {mode === key && (
                      <motion.span
                        layoutId="mode-pill"
                        className="absolute inset-0 rounded-full bg-white"
                        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                      />
                    )}
                    <span className="relative flex items-center gap-2">
                      <Vote className="h-4 w-4" />
                      {m.label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-8 space-y-3">
                {Object.entries(MODES).map(([key, m]) => (
                  <div
                    key={key}
                    className={clsx(
                      'flex items-start gap-4 rounded-2xl border p-4 transition-colors duration-300',
                      mode === key ? 'border-white/15 bg-white/[0.04]' : 'border-transparent'
                    )}
                  >
                    <span
                      className={clsx(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold tabular-nums transition-colors duration-300',
                        mode === key ? 'bg-brand-400 text-ink-950' : 'bg-white/[0.06] text-slate-400'
                      )}
                    >
                      {m.required}/{MEMBERS.length}
                    </span>
                    <div>
                      <p className={clsx('font-semibold', mode === key ? 'text-white' : 'text-slate-400')}>{m.label}</p>
                      <p className="mt-0.5 text-sm text-slate-500">{m.rule}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={() => setRunKey((k) => k + 1)} className="link-arrow mt-8">
                <RotateCcw className="h-4 w-4" />
                Replay approval
              </button>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div ref={cardRef}>
              <WithdrawalCard mode={mode} approvals={approvals} />
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};

export default GovernancePreview;
